/**
 * Defect Management page.
 * Report new defects via modal, view fleet-wide open defects with three-state status,
 * and filter defect history by aircraft.
 * Route: #/defects
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5
 */

import { getUser, getRole, isOwner } from '../services/auth.js';
import { getAircraft, getOpenDefects, getAllDefectsForAircraft, createFlight } from '../services/api.js';
import { reportDefect } from '../services/transactions.js';
import { navigate } from '../services/router.js';
import { formatDateUK } from '../utils/format.js';
import { DEFECT_STATUS, AIRCRAFT_STATUS } from '../utils/constants.js';
import { createDataGrid } from '../components/data-grid.js';
import { openDetailModal } from '../components/detail-modal.js';
import { getDefectStatusClass, getDefectStatusLabel, renderDefectStatusBadge } from '../components/defect-status-badge.js';

// ── Exported pure helpers for testability ─────────────

/**
 * Format a defect for display in the grid.
 * @param {Object} defect
 * @returns {Object} Formatted defect with display-friendly fields
 */
export function formatDefectForDisplay(defect) {
  if (!defect) return {};
  return {
    ...defect,
    display_title: defect.title || '—',
    display_description: defect.description || '—',
    display_aircraft: defect.aircraft?.registration || '—',
    display_reported_by: defect.pilots?.name || 'Unknown',
    display_date: defect.opened_at ? formatDateUK(defect.opened_at) : '—',
    display_status: defect.status || '—',
  };
}

/**
 * Filter defects by aircraft ID. Returns all defects if aircraftId is null/empty.
 * @param {Object[]} defects
 * @param {string|null} aircraftId
 * @returns {Object[]}
 */
export function filterDefectsByAircraft(defects, aircraftId) {
  if (!Array.isArray(defects)) return [];
  if (!aircraftId) return defects;
  return defects.filter((d) => d.aircraft_id === aircraftId);
}

/**
 * Filter defects by status. Returns all defects if status is null/empty.
 * @param {Object[]} defects
 * @param {string|null} status
 * @returns {Object[]}
 */
export function filterDefectsByStatus(defects, status) {
  if (!Array.isArray(defects)) return [];
  if (!status) return defects;
  return defects.filter((d) => d.status === status);
}

/**
 * Re-export getDefectStatusClass for testability from the page module.
 */
export { getDefectStatusClass } from '../components/defect-status-badge.js';

// ── Page state ────────────────────────────────────────

let aircraftList = [];
let allDefects = [];
let historyAircraftFilter = null;
let currentRole = null;

// ── Main render ───────────────────────────────────────

export async function renderDefects() {
  const app = document.getElementById('app');
  const user = getUser();
  currentRole = getRole();

  if (!user) { navigate('/login'); return; }

  const heading = currentRole === 'admin' ? 'Fleet Defect Management'
    : currentRole === 'owner' ? 'Aircraft Defects'
    : 'Defects — Report &amp; View';

  app.innerHTML = `
    <div class="container">
      <h2>${heading}</h2>
      <div id="defect-msg"></div>
      <div style="margin-bottom:1rem;">
        <button id="btn-report-defect" class="btn btn--danger">Report Defect</button>
      </div>
      <div class="card">
        <h3>Open Defects (Fleet-Wide)</h3>
        <div id="defect-list">Loading…</div>
      </div>
      <div class="card" style="margin-top:1rem;">
        <h3>Defect History</h3>
        <div style="margin-bottom:0.75rem;">
          <label for="history-aircraft-filter">Filter by Aircraft: </label>
          <select id="history-aircraft-filter">
            <option value="">All Aircraft</option>
          </select>
        </div>
        <div id="defect-history">Select an aircraft to view defect history.</div>
      </div>
    </div>
  `;

  // Load aircraft for dropdown and modal
  try {
    aircraftList = await getAircraft();
    const activeAircraft = aircraftList.filter((a) => {
      if (a.meta && a.meta.retired) return false;
      if (a.name && a.name.includes('[RETIRED]')) return false;
      return true;
    });
    const filterSel = document.getElementById('history-aircraft-filter');
    if (filterSel) {
      filterSel.innerHTML = '<option value="">All Aircraft</option>' +
        activeAircraft.map((a) => `<option value="${a.id}">${a.registration} (${a.type})</option>`).join('');
    }
  } catch (err) {
    showMsg('Could not load aircraft: ' + err.message, true);
  }

  await loadOpenDefects();

  // Report Defect button → modal
  const reportBtn = document.getElementById('btn-report-defect');
  if (reportBtn) {
    reportBtn.addEventListener('click', () => openReportDefectModal());
  }

  // History aircraft filter
  const filterSel = document.getElementById('history-aircraft-filter');
  if (filterSel) {
    filterSel.addEventListener('change', async () => {
      historyAircraftFilter = filterSel.value || null;
      await loadDefectHistory();
    });
  }

  // Auto-open Report Defect modal if navigated from dashboard quick action
  if (sessionStorage.getItem('auto-open-modal') === 'true') {
    sessionStorage.removeItem('auto-open-modal');
    openReportDefectModal();
  }
}

// ── Report Defect modal ───────────────────────────────

function getAircraftOptions() {
  return aircraftList
    .filter((ac) => {
      if (ac.meta && ac.meta.retired) return false;
      if (ac.name && ac.name.includes('[RETIRED]')) return false;
      return true;
    })
    .map((ac) => ({ value: ac.id, label: `${ac.registration} (${ac.type})` }));
}

function openReportDefectModal() {
  const fields = [
    { name: 'aircraft_id', label: 'Aircraft', type: 'select', options: getAircraftOptions(), required: true },
    { name: 'title', label: 'Defect Title', type: 'text', required: true, placeholder: 'Brief summary of the defect' },
    { name: 'description', label: 'Description', type: 'textarea', required: true, placeholder: 'Describe the defect in detail…' },
    {
      name: 'serviceability',
      label: 'Aircraft Serviceability',
      type: 'select',
      options: [
        { value: AIRCRAFT_STATUS.SERVICEABLE, label: 'Serviceable' },
        { value: AIRCRAFT_STATUS.UNSERVICEABLE, label: 'Unserviceable' },
      ],
      required: true,
    },
  ];

  openDetailModal({
    title: 'Report Defect',
    fields,
    onSave: async (formData) => {
      const user = getUser();
      const result = await reportDefect({
        aircraft_id: formData.aircraft_id,
        title: formData.title,
        description: formData.description,
        reported_by: user.id,
        serviceability: formData.serviceability,
      });

      if (result.error) {
        throw new Error(result.error);
      }

      const isUnserviceable = formData.serviceability === AIRCRAFT_STATUS.UNSERVICEABLE;
      showMsg(
        isUnserviceable
          ? 'Defect reported — aircraft marked UNSERVICEABLE.'
          : 'Defect reported successfully.',
        false,
      );
      await loadOpenDefects();
    },
  });
}

// ── Load and render open defects ──────────────────────

async function loadOpenDefects() {
  const listEl = document.getElementById('defect-list');
  if (!listEl) return;

  try {
    const defects = await getOpenDefects();
    allDefects = defects;

    if (!defects.length) {
      listEl.innerHTML = '<p style="color:var(--color-text-muted);">No open defects across the fleet.</p>';
      return;
    }

    listEl.innerHTML = '';

    const columns = [
      {
        key: 'title',
        label: 'Title',
        format: (v) => v || '—',
      },
      {
        key: 'aircraft_reg',
        label: 'Aircraft',
        html: true,
        format: (_, row) => {
          const reg = row.aircraft?.registration;
          const id = row.aircraft?.id || row.aircraft_id;
          if (!reg) return '—';
          if (!id) return reg;
          return `<a href="#/aircraft/${id}" class="aircraft-link">${reg}</a>`;
        },
      },
      {
        key: 'description',
        label: 'Description',
        format: (v) => {
          if (!v) return '—';
          return v.length > 80 ? v.slice(0, 80) + '…' : v;
        },
      },
      {
        key: 'opened_at',
        label: 'Reported',
        format: (v) => v ? formatDateUK(v) : '—',
      },
      {
        key: 'reported_by_name',
        label: 'Reported By',
        format: (_, row) => row.pilots?.name || 'Unknown',
      },
      {
        key: 'status',
        label: 'Status',
        html: true,
        format: (v) => renderDefectStatusBadge(v),
      },
    ];

    // Owner: Schedule Maintenance action per defect row
    const actions = currentRole === 'owner' ? [{
      label: 'Schedule Maintenance',
      className: 'btn--primary btn--sm',
      onClick: (row) => openScheduleMaintenanceModal(row),
    }] : undefined;

    const grid = createDataGrid({
      columns,
      data: defects,
      defaultSort: 'opened_at',
      defaultOrder: 'desc',
      emptyMessage: 'No open defects',
      filterable: true,
      actions,
    });

    listEl.appendChild(grid);
  } catch (err) {
    listEl.innerHTML = `<p style="color:var(--color-danger);">Error loading defects: ${err.message}</p>`;
  }
}

// ── Load and render defect history ────────────────────

async function loadDefectHistory() {
  const historyEl = document.getElementById('defect-history');
  if (!historyEl) return;

  if (!historyAircraftFilter) {
    historyEl.innerHTML = '<p style="color:var(--color-text-muted);">Select an aircraft to view defect history.</p>';
    return;
  }

  try {
    const defects = await getAllDefectsForAircraft(historyAircraftFilter);

    if (!defects.length) {
      historyEl.innerHTML = '<p style="color:var(--color-text-muted);">No defects found for this aircraft.</p>';
      return;
    }

    historyEl.innerHTML = '';

    const columns = [
      { key: 'title', label: 'Title', format: (v) => v || '—' },
      { key: 'description', label: 'Description', format: (v) => {
        if (!v) return '—';
        return v.length > 80 ? v.slice(0, 80) + '…' : v;
      }},
      { key: 'opened_at', label: 'Reported', format: (v) => v ? formatDateUK(v) : '—' },
      { key: 'reported_by_name', label: 'Reported By', format: (_, row) => row.pilots?.name || 'Unknown' },
      { key: 'status', label: 'Status', html: true, format: (v) => renderDefectStatusBadge(v) },
    ];

    const grid = createDataGrid({
      columns,
      data: defects,
      defaultSort: 'opened_at',
      defaultOrder: 'desc',
      emptyMessage: 'No defects found',
      filterable: true,
    });

    historyEl.appendChild(grid);
  } catch (err) {
    historyEl.innerHTML = `<p style="color:var(--color-danger);">Error loading defect history: ${err.message}</p>`;
  }
}

// ── Schedule Maintenance modal (owner only) ───────────

function openScheduleMaintenanceModal(defect) {
  const user = getUser();
  const fields = [
    {
      name: 'flight_date',
      label: 'Maintenance Date',
      type: 'date',
      required: true,
    },
    {
      name: 'start_time',
      label: 'Start Time',
      type: 'time',
      required: true,
    },
    {
      name: 'end_time',
      label: 'End Time',
      type: 'time',
      required: true,
    },
  ];

  openDetailModal({
    title: `Schedule Maintenance: ${defect.title}`,
    fields,
    onSave: async (formData) => {
      await createFlight({
        aircraft_id: defect.aircraft_id,
        pilot_id: user.id,
        flight_date: formData.flight_date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        purpose: `Maintenance: ${defect.title}`,
        status: 'APPROVED',
      });
      showMsg('Maintenance booking scheduled.', false);
    },
  });
}

// ── UI helpers ────────────────────────────────────────

function showMsg(text, isError) {
  const el = document.getElementById('defect-msg');
  if (!el) return;
  el.textContent = text;
  el.style.color = isError ? 'var(--color-danger)' : 'var(--color-success)';
  el.style.padding = '0.75rem';
  el.style.marginBottom = '1rem';
  if (!isError) {
    setTimeout(() => {
      el.textContent = '';
      el.style.padding = '';
      el.style.marginBottom = '';
    }, 5000);
  }
}
